import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

// Admin screen: which roles may open which tabs. A tick = the role sees the tab in the menu
// and its API routes answer; no tick = link hidden, route redirects, API returns 403.
// Admin always has every tab — its column is read-only (enforced on the backend too).

interface TabDef { key: string; label: string }
type Matrix = Record<string, Record<string, boolean>>;

const ROLE_ORDER = ['admin', 'kierownik_produkcji', 'user', 'stacja_pakowania', 'tracker'];

const RoleAccess: React.FC = () => {
  const { t } = useLanguage();
  const { refreshTabs } = useAuth();
  const [roles, setRoles] = useState<string[]>([]);
  // Names of the roles added in "Użytkownicy" (built-in roles are translated).
  const [roleNames, setRoleNames] = useState<Record<string, string>>({});
  const [tabs, setTabs] = useState<TabDef[]>([]);
  const [matrix, setMatrix] = useState<Matrix>({});
  const [saved, setSaved] = useState<Matrix>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const showToast = (kind: 'success' | 'error', text: string) => {
    setToast({ kind, text });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/role-tabs');
      const order = [...ROLE_ORDER.filter(r => res.data.roles.includes(r)), ...res.data.roles.filter((r: string) => !ROLE_ORDER.includes(r))];
      setRoles(order);
      setRoleNames(res.data.role_names ?? {});
      setTabs(res.data.tabs);
      setMatrix(res.data.matrix);
      setSaved(res.data.matrix);
    } catch (err: any) {
      showToast('error', err?.response?.data?.message ?? t('roleAccess.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => JSON.stringify(matrix) !== JSON.stringify(saved), [matrix, saved]);

  // Admin always has everything — the whole column is read-only.
  const isLocked = (role: string, _tab: string) => role === 'admin';

  const toggle = (role: string, tab: string) => {
    if (isLocked(role, tab)) return;
    setMatrix(prev => ({ ...prev, [role]: { ...prev[role], [tab]: !prev[role]?.[tab] } }));
  };


  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/role-tabs', { matrix });
      setMatrix(res.data.matrix);
      setSaved(res.data.matrix);
      showToast('success', res.data.message);
      refreshTabs();
    } catch (err: any) {
      showToast('error', err?.response?.data?.message ?? t('roleAccess.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm(t('roleAccess.confirmReset'))) return;
    setSaving(true);
    try {
      const res = await api.delete('/admin/role-tabs');
      setMatrix(res.data.matrix);
      setSaved(res.data.matrix);
      showToast('success', res.data.message);
      refreshTabs();
    } catch (err: any) {
      showToast('error', err?.response?.data?.message ?? t('roleAccess.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>🔐 {t('roleAccess.title')}</h1>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('roleAccess.subtitle')}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={reset} disabled={saving || loading} style={btnSecondary}>↺ {t('roleAccess.reset')}</button>
          <button onClick={save} disabled={saving || loading || !dirty} style={{ ...btnPrimary, opacity: saving || !dirty ? 0.5 : 1 }}>
            {saving ? '...' : `💾 ${t('roleAccess.save')}`}
          </button>
        </div>
      </div>

      {toast && (
        <div style={{ background: toast.kind === 'success' ? '#ecfdf5' : '#fef2f2', border: `1px solid ${toast.kind === 'success' ? '#6ee7b7' : '#fca5a5'}`, color: toast.kind === 'success' ? '#065f46' : '#991b1b', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
          {toast.text}
        </div>
      )}

      {dirty && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 14px', marginBottom: 16, fontSize: 13, color: '#92400e' }}>
          ⚠ {t('roleAccess.unsaved')}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={{ ...th, minWidth: 220 }}>{t('roleAccess.colTab')}</th>
              {roles.map(role => (
                <th key={role} style={{ ...th, textAlign: 'center', minWidth: 120 }}>
                  <div style={{ fontWeight: 700, color: '#1e293b' }}>{roleNames[role] ?? t(`roleAccess.roles.${role}`)}</div>
                  {role === 'admin' && <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400, marginTop: 2 }}>{t('roleAccess.adminAlways')}</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={roles.length + 1} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>{t('common.loading')}</td></tr>
            ) : tabs.map(tab => (
              <tr key={tab.key}>
                <td style={{ ...td, fontWeight: 600, color: '#1e293b' }}>
                  {tab.label}
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{tab.key}</div>
                </td>
                {roles.map(role => {
                  const on = !!matrix[role]?.[tab.key];
                  const lockedCell = isLocked(role, tab.key);
                  const changed = on !== !!saved[role]?.[tab.key];
                  return (
                    <td
                      key={role}
                      onClick={() => toggle(role, tab.key)}
                      style={{ ...td, textAlign: 'center', cursor: lockedCell ? 'not-allowed' : 'pointer', background: changed ? '#fffbeb' : undefined }}
                      title={lockedCell ? t('roleAccess.locked') : ''}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={lockedCell}
                        onChange={() => toggle(role, tab.key)}
                        onClick={e => e.stopPropagation()}
                        style={{ width: 20, height: 20, accentColor: '#667eea', cursor: lockedCell ? 'not-allowed' : 'pointer' }}
                      />

                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: 14, fontSize: 12, color: '#94a3b8' }}>{t('roleAccess.footnote')}</p>
    </div>
  );
};

const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#6c757d', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap', letterSpacing: 0.5, verticalAlign: 'top' };
const td: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' };
const btnPrimary: React.CSSProperties = { padding: '10px 18px', background: '#667eea', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { padding: '10px 14px', background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };

export default RoleAccess;
